import { useEffect, useState } from "react";
import { MapPin, Phone, CalendarCheck, Navigation } from "lucide-react";
import * as CookieConsent from "vanilla-cookieconsent";
import { SITE, PHOTOS } from "../lib/site";
import MapPlaceholder from "./MapPlaceholder";
import { Link } from "react-router-dom";

export default function Contact() {
  const [mapAccepted, setMapAccepted] = useState(false);

  useEffect(() => {
    const check = () => {
      try {
        setMapAccepted(
          CookieConsent.acceptedCategory &&
            CookieConsent.acceptedCategory("functionality"),
        );
      } catch (_) {
        setMapAccepted(false);
      }
    };
    check();
    window.addEventListener("cc:onConsent", check);
    window.addEventListener("cc:onChange", check);
    return () => {
      window.removeEventListener("cc:onConsent", check);
      window.removeEventListener("cc:onChange", check);
    };
  }, []);

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      translate="no"
      lang="it"
      className="notranslate relative py-24 sm:py-32 bg-[var(--ee-cream)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="ee-eyebrow">Dove siamo</div>
          <h2
            data-testid="contact-title"
            className="font-serif-display mt-4 text-[34px] sm:text-[46px] lg:text-[54px] leading-[1.08] tracking-[-0.01em] text-[var(--ee-ink)]"
          >
            Vieni a trovarci
            <em className="italic text-[var(--ee-rose-strong)]"> a Mestre</em>.
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Info card */}
          <div
            data-testid="contact-card"
            className="lg:col-span-5 ee-card bg-white p-8 sm:p-10 flex flex-col gap-7"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ee-rose-strong)] font-medium">
                Centro estetico
              </div>
              <div className="font-serif-display text-[30px] sm:text-[34px] mt-1 leading-tight text-[var(--ee-ink)]">
                {SITE.name}
              </div>
            </div>

            <InfoRow icon={<MapPin size={17} strokeWidth={1.7} />} label="Indirizzo">
              <span className="leading-relaxed">
                Via Don Federico Tosatto 22
                <br />
                30174 Venezia (Mestre)
              </span>
            </InfoRow>

            <InfoRow icon={<Phone size={17} strokeWidth={1.7} />} label="Telefono">
              <a
                href={SITE.phoneTel}
                data-testid="contact-phone-link"
                className="hover:text-[var(--ee-rose-strong)] transition-colors"
              >
                {SITE.phone}
              </a>
            </InfoRow>

            <InfoRow icon={<CalendarCheck size={17} strokeWidth={1.7} />} label="Prenotazione">
              <Link
                to="/prenota"
                data-testid="contact-treatwell-link"
                className="hover:text-[var(--ee-rose-strong)] transition-colors"
              >
                Prenota online — disponibilità in tempo reale
              </Link>
            </InfoRow>

            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href={SITE.maps}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-btn"
                className="ee-btn ee-btn-outline !py-2.5 !px-4 text-[13px]"
              >
                <Navigation size={15} strokeWidth={1.8} />
                Apri indicazioni
              </a>
              <a
                href={SITE.phoneTel}
                data-testid="contact-call-btn"
                className="ee-btn ee-btn-ghost !py-2.5 !px-4 text-[13px]"
              >
                <Phone size={15} strokeWidth={1.8} />
                Chiama
              </a>
              <Link
                to="/prenota"
                data-testid="contact-book-btn"
                className="ee-btn ee-btn-primary !py-2.5 !px-4 text-[13px]"
              >
                <CalendarCheck size={15} strokeWidth={1.8} />
                Prenota
              </Link>
            </div>
          </div>

          {/* Foto + Map */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div
              data-testid="contact-photo"
              className="ee-img-frame aspect-[16/9] overflow-hidden"
            >
              <img
                src={PHOTOS.portaLineArt}
                alt="Dettaglio Estetica Elite"
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center" }}
              />
            </div>
            <div
              data-testid="contact-map"
              className="ee-card overflow-hidden bg-white p-0 min-h-[300px] lg:min-h-[360px] flex-1"
            >
              {mapAccepted ? (
                <iframe
                  data-category="functionality"
                  data-src={SITE.mapsEmbed}
                  src={SITE.mapsEmbed}
                  title="Mappa Estetica Elite — Via Don Federico Tosatto 22, Mestre"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 300, display: "block" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <MapPlaceholder />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const InfoRow = ({ icon, label, children }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 rounded-full bg-[var(--ee-blush-soft)] flex items-center justify-center text-[var(--ee-rose-strong)] flex-shrink-0">
      {icon}
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ee-ink-soft)] font-medium">
        {label}
      </div>
      <div className="mt-1 text-[14.5px] text-[var(--ee-ink)]">{children}</div>
    </div>
  </div>
);
